import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import EditorJS, { OutputData } from '@editorjs/editorjs'
import Header from '@editorjs/header';
import List from '@editorjs/list';
import Table from '@editorjs/table';
import ImageTool from "@editorjs/image";
import CodeTool from "@editorjs/code";
import WarningTool from '@editorjs/warning';
import Delimiter from "@editorjs/delimiter";
import Quote from '@editorjs/quote';
import { NgIf } from "@angular/common";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { faEdit, faSave } from "@fortawesome/free-solid-svg-icons";
import { EditorjsDraftService } from "../../../_services/editorjs-draft.service";

@Component({
  selector: 'hawk-editor-js',
  imports: [
    NgIf,
    FaIconComponent
  ],
  templateUrl: './editor-js.component.html',
  styleUrl: './editor-js.component.scss'
})
export class EditorJsComponent implements AfterViewInit, OnDestroy, OnChanges {

  @Input() initialData!: OutputData;
  @Input() byFileEndpoint!: string;
  @Output() saveContent = new EventEmitter<{ data: OutputData, draft_id: string }>();

  @ViewChild('editorContainer', { static: true }) editorContainer!: ElementRef;

  editor!: EditorJS;
  isReadOnly = true;
  lastSavedData!: OutputData;
  workingData!: OutputData;
  draftId!: string;

  constructor(
    private draftService: EditorjsDraftService,
  ) {
  }

  ngAfterViewInit(): void {
    this.initData()
    this.initEditor(this.lastSavedData, true);
  }

  initData(): void {
    this.draftId = this.draftService.getOrCreateDraft();
    this.lastSavedData = this.clone(this.initialData);
    this.workingData   = this.clone(this.initialData);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes['initialData'] && !changes['initialData'].firstChange)
    ) {
      this.initData()
      this.reinitEditor(this.initialData, true);
    } else if (
      (changes['byFileEndpoint'] && !changes['byFileEndpoint'].firstChange)
    ) {
      this.reinitEditor(this.initialData, true);
    }
  }

  private reinitEditor(data: OutputData, readOnly: boolean) {
    this.destroyEditor()
    this.initEditor(data, readOnly);
  }

  private destroyEditor() {
    if (this.editor && this.editor.destroy) this.editor.destroy();
  }

  initEditor(data: OutputData, readOnly: boolean): void {
    this.destroyEditor()

    this.editor = new EditorJS({
      holder: this.editorContainer.nativeElement,
      readOnly: readOnly,
      data: data || { blocks: [] },
      tools: {
        header: Header,
        list: List,
        table: Table,
        image: this.byFileEndpoint ? {
          class: ImageTool,
          config: {
            uploader: { uploadByFile: (file: File) => this.uploadImage(file) }
          }
        } : undefined,
        code: CodeTool,
        warning: WarningTool,
        delimiter: Delimiter,
        quote: {
          class: Quote,
          inlineToolbar: true,
          config: {
            quotePlaceholder: 'Zitat eingeben',
            captionPlaceholder: 'Autor'
          }
        }
      },
      autofocus: true,
      onReady: () => {
        console.log('Editor ready (mode:', this.isReadOnly ? 'readonly' : 'edit', ')');
      }
    });
    this.isReadOnly = readOnly
  }

  private uploadImage(file: File): Promise<{ success:1, file:{ url: string } }> {
    return new Promise<{ success:1, file:{ url: string } }>(resolve => {
      this.draftService.saveDraftImage(this.draftId, file).subscribe(
        res => {
          resolve(res);
        },
        err => {
          console.log(err);
          resolve(null)
        }
      )
    })
  }

  reinitDrafts(): void {
    this.draftService.clearDraft();
    this.draftId = this.draftService.getOrCreateDraft();
  }

  async startEdit() {
    this.workingData = this.clone(this.lastSavedData);
    this.reinitEditor(this.workingData, false);
  }

  async save(): Promise<void> {
    const output = await this.editor.save();
    this.lastSavedData = this.clone(output);
    this.reinitEditor(this.lastSavedData, true);
    this.saveContent.emit({ data: output, draft_id: this.draftId });
    console.log('Saved content:', output);
  }

  async cancelEdit() {
    this.reinitDrafts()
    this.reinitEditor(this.lastSavedData, true);
  }

  private clone<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
  }

  ngOnDestroy(): void {
    this.destroyEditor()
  }

  protected readonly faEdit = faEdit;
  protected readonly faSave = faSave;
}
